<template>
    <div v-if="isVisible" class="support-fab" :class="{ 'support-fab--open': isOpen }">
        <transition name="support-fab-panel">
            <div
                v-if="isOpen"
                ref="panel"
                class="support-fab__panel"
                role="dialog"
                :aria-label="$t('support_chat')"
            >
                <div class="support-fab__panel-header">
                    <div>
                        <p class="support-fab__eyebrow mb-1">{{ $t('support_chat') }}</p>
                        <h6 class="mb-0">{{ supportName }}</h6>
                    </div>
                    <button
                        type="button"
                        class="support-fab__close"
                        :aria-label="$t('close')"
                        @click="isOpen = false"
                    >
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <p class="support-fab__hint mb-3">
                    {{ $t('support_chat_coming_soon_hint') }}
                </p>

                <div class="support-fab__details">
                    <div v-if="supportEmail" class="support-fab__detail">
                        <span class="support-fab__detail-label">{{ $t('support_email') }}</span>
                        <a :href="`mailto:${supportEmail}`" class="support-fab__detail-link">{{ supportEmail }}</a>
                    </div>
                    <div v-if="supportPhone" class="support-fab__detail">
                        <span class="support-fab__detail-label">{{ $t('support_phone') }}</span>
                        <a :href="`tel:${supportPhone}`" class="support-fab__detail-link">{{ supportPhone }}</a>
                    </div>
                </div>

                <div class="support-fab__actions">
                    <button
                        v-if="canWriteSupport"
                        type="button"
                        class="btn btn-primary btn-sm"
                        @click="writeSupport"
                    >
                        {{ $t('write_support') }}
                    </button>
                    <a
                        v-if="supportPhone"
                        :href="`tel:${supportPhone}`"
                        class="btn btn-outline-secondary btn-sm"
                    >
                        {{ $t('call_support') }}
                    </a>
                </div>
            </div>
        </transition>

        <button
            ref="button"
            type="button"
            class="support-fab__button"
            :aria-expanded="isOpen ? 'true' : 'false'"
            :aria-label="$t('support_chat')"
            @click="toggle"
        >
            <i class="fas fa-comments"></i>
        </button>
    </div>
</template>

<script>
import { getSupportConfig, hasSupportChannel, openSupport } from "../../helpers/app/SupportLauncher";

export default {
    name: "FloatingSupportButton",
    data() {
        return {
            isOpen: false,
        };
    },
    computed: {
        supportConfig() {
            return getSupportConfig();
        },
        supportName() {
            return this.supportConfig.name || this.$t('support_team');
        },
        supportEmail() {
            return this.supportConfig.email || '';
        },
        supportPhone() {
            return this.supportConfig.phone || '';
        },
        isVisible() {
            return Boolean(this.supportEmail || this.supportPhone || hasSupportChannel());
        },
        canWriteSupport() {
            return Boolean(this.supportEmail || hasSupportChannel());
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleEscape);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleEscape);
    },
    methods: {
        toggle() {
            this.isOpen = !this.isOpen;
        },
        writeSupport() {
            openSupport({
                title: this.$t('support_request'),
                description: this.$t('support_chat_coming_soon_hint'),
            });
        },
        handleDocumentClick(event) {
            if (!this.isOpen) {
                return;
            }

            const button = this.$refs.button;
            const panel = this.$refs.panel;

            if (button?.contains(event.target) || panel?.contains(event.target)) {
                return;
            }

            this.isOpen = false;
        },
        handleEscape(event) {
            if (event.key === 'Escape') {
                this.isOpen = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.support-fab {
    position: fixed;
    right: 1.5rem;
    bottom: 1.5rem;
    z-index: 1085;
}

.support-fab__button {
    width: 3.75rem;
    height: 3.75rem;
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, var(--brand-primary), var(--brand-primary-hover));
    color: #fff;
    box-shadow: var(--logistics-floating-shadow);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    transition: transform 180ms ease, box-shadow 180ms ease;
}

.support-fab__button:hover,
.support-fab__button:focus {
    outline: none;
    transform: translateY(-2px);
    box-shadow: 0 22px 42px rgba(var(--brand-primary-rgb), 0.28);
}

.support-fab__panel {
    width: min(20rem, calc(100vw - 2rem));
    margin-bottom: 0.9rem;
    margin-left: auto;
    padding: 1rem;
    border: 1px solid rgba(var(--brand-primary-rgb), 0.14);
    border-radius: 1.25rem;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: var(--logistics-floating-shadow);
    backdrop-filter: blur(16px);
}

.support-fab__panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.support-fab__eyebrow {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--brand-primary);
}

.support-fab__close {
    width: 2rem;
    height: 2rem;
    border: 0;
    border-radius: 999px;
    background: rgba(var(--brand-primary-rgb), 0.1);
    color: var(--brand-primary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.support-fab__hint {
    color: var(--secondary);
    line-height: 1.45;
}

.support-fab__details {
    display: grid;
    gap: 0.85rem;
    margin-bottom: 1rem;
}

.support-fab__detail {
    padding: 0.75rem 0.85rem;
    border-radius: 0.9rem;
    background: rgba(var(--brand-primary-rgb), 0.06);
}

.support-fab__detail-label {
    display: block;
    font-size: 0.74rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--secondary);
    margin-bottom: 0.3rem;
}

.support-fab__detail-link {
    color: var(--default-primary);
    font-weight: 600;
    word-break: break-word;
}

.support-fab__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
}

.support-fab-panel-enter-active,
.support-fab-panel-leave-active {
    transition: opacity 160ms ease, transform 160ms ease;
}

.support-fab-panel-enter,
.support-fab-panel-leave-to {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
}

[theme='dark'] .support-fab__panel {
    background: rgba(15, 23, 42, 0.94);
    border-color: rgba(var(--brand-primary-rgb), 0.22);
}

[theme='dark'] .support-fab__hint,
[theme='dark'] .support-fab__detail-label {
    color: rgba(226, 232, 240, 0.76);
}

[theme='dark'] .support-fab__detail {
    background: rgba(var(--brand-primary-rgb), 0.12);
}

[theme='dark'] .support-fab__detail-link {
    color: #fff;
}

@media (max-width: 575.98px) {
    .support-fab {
        right: 1rem;
        bottom: 1rem;
    }

    .support-fab__button {
        width: 3.35rem;
        height: 3.35rem;
    }
}
</style>
